import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ROUTES_CONFIG } from "@/config/routes.config";
import { useTRPC } from "@/trpc";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { TRPCClientError } from "@trpc/client";
import React from 'react'
import { Controller, useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import * as Icons from "lucide-react";

interface Props {
  trigger?: React.ReactNode;
  onCreated?: () => void;
}

const createUserSchema = z.object({
    email: z.string().email().min(1, "Email is required"),
    role: z.enum(["head", "scout", "screener"])
})

type CreateUserForm = z.infer<typeof createUserSchema>

const CreateUser = ({ trigger, onCreated }: Props) => {
    const { trpc } = useTRPC();
    const form = useForm<CreateUserForm>({
        resolver: zodResolver(createUserSchema),
    })

    const inviteUserMutation = useMutation({
        mutationFn: async (data:CreateUserForm) => {
            const invitation = await trpc.users.invite.mutate({
                email: data.email,
                role: data.role as any
            })
            return invitation
        },
        onSuccess: () => {
            onCreated?.()
        },
        onError: (err) => {
            if(err instanceof TRPCClientError) {
                toast.error(err.message)
            } else if (err instanceof Error) {
                toast.error(err.message)
            } else {
                toast.error("Er is een onbekende fout opgetreden")
            }
        }
    })

    const handleSubmit = () => {
        const values = form.getValues();
        inviteUserMutation.mutate(values)
    }

  return (
    <AlertDialog>
        <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>
        <AlertDialogContent>
            {inviteUserMutation.isSuccess
            ?
            <>
            <AlertDialogHeader>
                <AlertDialogTitle>Gebruiker aangemaakt!</AlertDialogTitle>
                <AlertDialogDescription>Verstuur onderstaande link naar de gebruiker om de uitnodiging te accepteren</AlertDialogDescription>
            </AlertDialogHeader>
            <div>
                <div className="flex items-center gap-2">
                    <Input 
                        defaultValue={`${window.location.origin}${ROUTES_CONFIG.AUTH.REGISTER.$buildPath({ searchParams: { token:inviteUserMutation.data?.token } })}`}
                    />
                    <Button 
                        onClick={()=>{ 
                            navigator.clipboard.writeText(`${window.location.origin}${ROUTES_CONFIG.AUTH.REGISTER.$buildPath({ searchParams: { token:inviteUserMutation.data?.token } })}`) 
                            toast.success("Link gekopieerd naar klembord")
                        }}
                        variant='outline' 
                    >
                        <Icons.Copy className="w-4 h-4"/>
                    </Button>
                </div>
            </div>
            <AlertDialogFooter>
                <AlertDialogCancel>Sluiten</AlertDialogCancel>
            </AlertDialogFooter>
            </>
            :
            <>
            <AlertDialogHeader>
                <AlertDialogTitle>Nieuwe gebruiker</AlertDialogTitle>
                <AlertDialogDescription>Vul de velden in om een nieuwe gebruiker toe te voegen</AlertDialogDescription>
            </AlertDialogHeader>
            <div className="flex flex-col gap-4">
                <div>
                    <Label htmlFor="email">Email</Label>
                    <Controller 
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                            <Input value={field.value} onChange={(e)=>{ field.onChange(e.currentTarget.value) }} type="email" id="email"/>
                        )}
                    />
                </div>
                <div>
                    <Label htmlFor="role">Rol</Label>
                    <Controller 
                        control={form.control}
                        name="role"
                        render={({ field }) => (
                            <Select
                                value={field.value}
                                onValueChange={(value)=>{field.onChange(value)}}
                            >
                                <SelectTrigger id="role">
                                    <SelectValue placeholder="Selecteer rol" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="head">Hoofdscout</SelectItem>
                                    <SelectItem value="scout">Scout</SelectItem>
                                    <SelectItem value="screener">Screener</SelectItem>
                                </SelectContent>
                            </Select>
                        )}
                    />
                    
                </div>
            </div>
            <AlertDialogFooter>
                <AlertDialogCancel>Annuleren</AlertDialogCancel>
                <Button onClick={()=>{handleSubmit()}} disabled={!form.formState.isValid || inviteUserMutation.isLoading}>
                    {inviteUserMutation.isLoading && <Icons.Loader2 className="w-4 h-4 animate-spin" />}
                    Aanmaken
                </Button>
            </AlertDialogFooter>
            </>
            }

            
        </AlertDialogContent>
    </AlertDialog>
  )
}

export default CreateUser